import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface TicketMessage {
  id: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
  authorId: string;
}

interface Ticket {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  messages: TicketMessage[];
}

const statusLabels: Record<string, string> = {
  open: "Aberto",
  in_progress: "Em Andamento",
  waiting_client: "Aguardando Cliente",
  waiting_support: "Aguardando Suporte",
  resolved: "Resolvido",
  closed: "Fechado",
};

const statusColors: Record<string, string> = {
  open: "bg-accent-100 text-accent-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  waiting_client: "bg-orange-100 text-orange-800",
  waiting_support: "bg-accent-100 text-accent-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-800",
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch(`/api/tickets/${id}`)
      .then((json) => {
        if (json.success) {
          setTicket(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);

    try {
      const json = await apiFetch(`/api/tickets/${id}/messages`, {
        method: "POST",
        body: JSON.stringify({ content: newMessage }),
      });
      if (json.success) {
        setNewMessage("");
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Chamado não encontrado</h2>
          <a href="/chamados" className="mt-4 text-accent-600 hover:underline">
            Voltar para Chamados
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium text-accent-600">Chamados</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-4">
            <a href="/chamados" className="text-sm text-gray-600 hover:text-accent-600 dark:text-gray-400">
              ← Voltar
            </a>
            <Badge className={statusColors[ticket.status]}>
              {statusLabels[ticket.status]}
            </Badge>
          </div>

          <h1 className="mt-4 text-3xl font-bold">{ticket.title}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {ticket.description}
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Criado em {new Date(ticket.createdAt).toLocaleDateString("pt-BR")}
          </div>

          <div className="mt-8 space-y-4">
            <h2 className="text-xl font-semibold">Mensagens</h2>

            {ticket.messages.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400">
                Nenhuma mensagem ainda.
              </p>
            ) : (
              ticket.messages.map((message) => (
                <div
                  key={message.id}
                  className={`rounded-2xl border p-4 ${
                    message.isInternal
                      ? "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20"
                      : "border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {message.isInternal ? "Suporte (Interno)" : "Você"}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(message.createdAt).toLocaleDateString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="mt-2">{message.content}</p>
                </div>
              ))
            )}
          </div>

          {ticket.status !== "closed" && (
            <form onSubmit={handleSendMessage} className="mt-8">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="w-full resize-none border-0 bg-transparent focus:outline-none"
                  placeholder="Digite sua mensagem..."
                />
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                  >
                    {sending ? "Enviando..." : "Enviar"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
